import Cookies from 'js-cookie';
import type { IncomingHttpHeaders } from 'http';
import type { NextApiResponse } from 'next';
import { AUTH_KEYS } from '@/constants/common';
import { fuelTypeEnum } from '@/components/fakeDoor/constants';
import Router from '@/components/common/router';
import { FilteredCarDetails } from '@/types';

export const hasItems = (arr: any[]): boolean => {
    return Array.isArray(arr) && arr.length > 0;
};

interface StatusUpdate {
    title: string;
    timestamp: string;
    status: string;
    isActive: boolean;
}

export function filterDuplicates(updates: StatusUpdate[]): StatusUpdate[] {
    if (!hasItems(updates)) return [];
    const seenTitles = new Set<string>();
    return updates.filter((update) => {
        if (seenTitles.has(update.title)) {
            return false;
        }
        seenTitles.add(update.title);
        return true;
    });
}

export function isAmountPositive(discountValue?: number) {
    if (discountValue === null || discountValue === undefined) {
        return false;
    }

    if (typeof discountValue !== 'number') {
        return false;
    }

    return discountValue > 0;
}

export const noop = () => {};

export const getUserIdFromCookie = (req?: any): string => {
    try {
        let rawTokens: string | undefined;
        if (req) {
            rawTokens = req.cookies.ackodrive_tokens;
        } else {
            rawTokens = Cookies.get('ackodrive_tokens');
        }
        const tokens = rawTokens && JSON.parse(rawTokens);
        return tokens?.access || '';
    } catch (error) {
        console.error('Failed to parse ackodrive_tokens cookie:', error);
        return '';
    }
};

export const getHeaderString = (
    header: string | string[] | undefined
): string => {
    if (Array.isArray(header)) return header[0];
    return header || '';
};

export function handleApiError(
    res: NextApiResponse,
    error: unknown,
    contextMessage = 'Unexpected server error'
) {
    console.error(`${contextMessage}:`, error);
    res.status(500).json({
        message: error instanceof Error ? error.message : contextMessage,
    });
}

export function getCommonHeaders(headers: IncomingHttpHeaders) {
    return {
        'Content-Type': 'application/json',
        'x-auth-user-id': getHeaderString(headers[AUTH_KEYS.X_AUTH_USER_ID]),
        'x-user-id': getHeaderString(headers[AUTH_KEYS.X_USER_ID]),
        cookie: headers.cookie || '',
    };
}

export const isClient = () => typeof window !== 'undefined';

export const redirectToLandingPage = () => {
    if (isClient()) {
        Router.push('/');
    }
};

export const getMonthYear = (dateString: string = '') => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return { month, year };
};

export const checkObjectEmpty = (obj: any) => Object.keys(obj).length === 0;

export const convertFuelTypeUsingEnum = (fuel: string): string | null => {
    const upperFuel = fuel.toUpperCase();
    const match = fuelTypeEnum.find((f) => f.id === upperFuel);
    return match ? match.name : null;
};

export function filterCarDetails(details: any): FilteredCarDetails {
    if (!details) return {};

    const {
        acko_model_id,
        model_name,
        make_name,
        fuel_type,
        registration_year,
        color,
        acko_variant_id,
        variant_name,
        registration_number,
    } = details;

    return {
        acko_model_id,
        model_name,
        make_name,
        fuel_type,
        registration_year,
        color,
        acko_variant_id,
        variant_name,
        registration_number,
    };
}
